import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import type { MockTool } from '@/mocks/tools';
import { toolService } from '@/services/toolService';
import { getAllMachines, type MachineResponse } from '@/api/machine.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

const CATEGORY_LABELS: Record<MockTool['category'], string> = {
  DIE: 'Die',
  FIXTURE: 'Fixture',
  JIGS: 'Jigs',
  CUTTING_TOOL: 'Cutting Tool',
  MOULD: 'Mould',
  GAUGE: 'Gauge',
};

const STATUS_BADGES: Record<string, { color: string; label: string }> = {
  ACTIVE: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Active' },
  UNDER_REPAIR: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Under Repair' },
  RETIRED: { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Retired' },
  SCRAPPED: { color: 'bg-rose-50 text-rose-700 border-rose-200 line-through', label: 'Scrapped' },
};

export default function ToolListPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);

  const [tools, setTools] = useState<MockTool[]>([]);
  const [machines, setMachines] = useState<MachineResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [machineFilter, setMachineFilter] = useState<string>('ALL');

  const categoryFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Categories' },
    ...Object.entries(CATEGORY_LABELS).map(([k, v]) => ({ value: k, label: v }))
  ], []);

  const statusFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Status' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'UNDER_REPAIR', label: 'Under Repair' },
    { value: 'RETIRED', label: 'Retired' },
    { value: 'SCRAPPED', label: 'Scrapped' }
  ], []);

  const machineFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Machines' },
    { value: 'NONE', label: 'Unassigned' },
    ...machines.map((m) => ({ value: m.id, label: m.name }))
  ], [machines]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [toolsData, machinesRes] = await Promise.all([
        toolService.getTools({ warehouseId: selectedWarehouseId || undefined }),
        getAllMachines(selectedWarehouseId || undefined).catch(() => ({ success: false, data: [] })),
      ]);
      setTools(toolsData || []);
      if (machinesRes.success && machinesRes.data) {
        const mList = selectedWarehouseId && selectedWarehouseId !== 'ALL'
          ? machinesRes.data.filter((m: any) => m.warehouse_id === selectedWarehouseId || m.warehouseId === selectedWarehouseId)
          : machinesRes.data;
        setMachines(mList);
      }
    } catch (err) {
      console.error('Error loading tools:', err);
      toast.error('Failed to load tools from server');
      setTools([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWarehouseId]);

  const getLifePercent = (tool: MockTool): number => {
    if (tool.maxShotCount > 0) return (tool.currentShotCount / tool.maxShotCount) * 100;
    if (tool.maxLifeHours && tool.maxLifeHours > 0) return (tool.currentLifeHours / tool.maxLifeHours) * 100;
    return 0;
  };

  const summary = useMemo(() => {
    const active = tools.filter((t) => t.status === 'ACTIVE').length;
    const critical = tools.filter((t) => {
      if (t.status !== 'ACTIVE') return false;
      const pct = getLifePercent(t);
      return pct >= t.alertThresholdPercent || t.remainingShots < 5000;
    }).length;
    const underRepair = tools.filter((t) => t.status === 'UNDER_REPAIR').length;
    const totalInvest = tools.reduce((s, t) => s + (t.purchaseCost || 0), 0);
    return { active, critical, underRepair, totalInvest };
  }, [tools]);

  const criticalTools = useMemo(
    () => tools.filter((t) => t.status === 'ACTIVE' && (getLifePercent(t) > 90 || t.remainingShots < 2000)),
    [tools]
  );

  const filtered = useMemo(() => {
    return tools.filter((t) => {
      if (selectedWarehouseId && selectedWarehouseId !== 'ALL') {
        const toolWh = (t as any).warehouseId || (t as any).warehouse_id;
        if (toolWh && toolWh !== selectedWarehouseId) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!t.toolNumber.toLowerCase().includes(q) && !t.name.toLowerCase().includes(q)) return false;
      }
      if (categoryFilter !== 'ALL' && t.category !== categoryFilter) return false;
      if (statusFilter !== 'ALL' && t.status !== statusFilter) return false;
      if (machineFilter !== 'ALL') {
        if (machineFilter === 'NONE' && t.assignedMachineId !== null) return false;
        if (machineFilter !== 'NONE' && t.assignedMachineId !== machineFilter) return false;
      }
      return true;
    });
  }, [tools, search, categoryFilter, statusFilter, machineFilter, selectedWarehouseId]);

  const handleSendRepair = async (tool: MockTool) => {
    try {
      await toolService.sendForRepair(tool.id, {
        repairType: 'MAINTENANCE',
        repairBy: 'In-House Workshop',
        description: 'Sent for routine maintenance',
      });
      toast.success(`${tool.toolNumber} sent for repair`);
      loadData();
    } catch (err) {
      console.error('Error sending tool for repair:', err);
      toast.error('Failed to send tool for repair');
    }
  };

  const handleRetire = async (tool: MockTool) => {
    try {
      await toolService.retireTool(tool.id, 'RETIRED');
      toast.success(`${tool.toolNumber} retired`);
      loadData();
    } catch (err) {
      console.error('Error retiring tool:', err);
      toast.error('Failed to retire tool');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('ALL');
    setStatusFilter('ALL');
    setMachineFilter('ALL');
  };

  const hasFilters = search || categoryFilter !== 'ALL' || statusFilter !== 'ALL' || machineFilter !== 'ALL';
  const formatCurrency = (v: number) => '\u20B9' + v.toLocaleString('en-IN');

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Tool &amp; Die Management</h1>
              <p className="text-sm text-[#64748b] mt-0.5">Track tool life, usage, and maintenance cycles</p>
            </div>
            
            {/* Top Right Actions */}
            <div className="flex items-center gap-2.5 flex-wrap sm:ml-auto">
              <button
                onClick={() => navigate('/manufacturing/tools/new')}
                className="px-4 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
              >
                <i className="ri-add-line text-base" /> Add Tool
              </button>
            </div>
          </div>

          {/* Critical Alert Banner */}
          {criticalTools.length > 0 && (
            <div className="mb-6 px-4 py-3.5 rounded-xl bg-rose-50 border border-rose-300 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                  <i className="ri-alert-line text-lg text-rose-600" />
                </div>
                <span className="text-sm font-bold text-rose-800">
                  {criticalTools.length} tool{criticalTools.length > 1 ? 's are' : ' is'} near end of life — order replacements
                </span>
              </div>
              <button
                onClick={() => {
                  setStatusFilter('ALL');
                  setSearch('');
                  setCategoryFilter('ALL');
                }}
                className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold cursor-pointer whitespace-nowrap shadow-sm"
              >
                View Critical Tools
              </button>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 shrink-0">
                  <i className="ri-check-double-line text-xl" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{summary.active}</p>
                  <p className="text-xs font-semibold text-emerald-600 mt-0.5">Active Tools</p>
                </div>
              </div>
            </div>
            
            <div className={`bg-white border rounded-xl p-5 shadow-sm ${summary.critical > 0 ? 'border-rose-300 bg-rose-50/30' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-rose-100 text-rose-600 shrink-0">
                  <i className="ri-alert-line text-xl" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rose-700">{summary.critical}</p>
                  <p className="text-xs font-semibold text-rose-600 mt-0.5">Critical (&gt;80% life)</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 shrink-0">
                  <i className="ri-tools-line text-xl" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{summary.underRepair}</p>
                  <p className="text-xs font-semibold text-amber-600 mt-0.5">Under Repair</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 shrink-0">
                  <i className="ri-money-rupee-circle-line text-xl" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-700">{formatCurrency(summary.totalInvest)}</p>
                  <p className="text-xs font-semibold text-indigo-600 mt-0.5">Total Investment</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                    <i className="ri-search-line text-sm" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by tool number or name..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <div className="w-44">
                  <SearchableSelect
                    value={categoryFilter}
                    onChange={(val) => setCategoryFilter(val)}
                    options={categoryFilterOptions}
                    placeholder="All Categories"
                  />
                </div>
                <div className="w-40">
                  <SearchableSelect
                    value={statusFilter}
                    onChange={(val) => setStatusFilter(val)}
                    options={statusFilterOptions}
                    placeholder="All Status"
                  />
                </div>
                <div className="w-48">
                  <SearchableSelect
                    value={machineFilter}
                    onChange={(val) => setMachineFilter(val)}
                    options={machineFilterOptions}
                    placeholder="All Machines"
                    searchPlaceholder="Search machine..."
                  />
                </div>
              </div>
            </div>
            {hasFilters && (
              <div className="flex justify-end mt-3 border-t border-slate-100 pt-2">
                <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-800 cursor-pointer underline font-medium">
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Tool No</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Category</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Machine</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Life Used %</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Remaining</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">
                        <div className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin text-xl text-blue-600" />
                          <span>Loading tool assets from database...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">
                        <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-3">
                          <i className="ri-inbox-line text-xl text-slate-300" />
                        </div>
                        No tools found in database matching your filters
                      </td>
                    </tr>
                  ) : (
                    filtered.map((tool) => {
                      const lifePct = getLifePercent(tool);
                      const barColor = lifePct < 60 ? 'bg-emerald-500' : lifePct < 80 ? 'bg-amber-500' : 'bg-rose-500';
                      const remainingLabel = tool.maxLifeHours && tool.maxLifeHours > 0
                        ? `${(tool.maxLifeHours - tool.currentLifeHours).toFixed(0)} hrs`
                        : (tool.remainingShots || 0).toLocaleString();
                      const remainingDanger = tool.maxLifeHours && tool.maxLifeHours > 0
                        ? (tool.maxLifeHours - tool.currentLifeHours) < 100
                        : (tool.remainingShots || 0) < 5000;
                      const remainingWarn = tool.maxLifeHours && tool.maxLifeHours > 0
                        ? (tool.maxLifeHours - tool.currentLifeHours) < 500
                        : (tool.remainingShots || 0) < 20000;
                      const badge = STATUS_BADGES[tool.status] || { color: 'bg-slate-100 text-slate-600 border-slate-200', label: tool.status };
                      return (
                        <tr key={tool.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-bold text-[#2563eb]">{tool.toolNumber}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-[#1e293b]">{tool.name}</div>
                            <div className="text-[11px] text-slate-400 font-medium">{tool.type}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-slate-50 text-slate-600 border-slate-200">
                              {CATEGORY_LABELS[tool.category] || tool.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-slate-700">
                            {tool.assignedMachineName ?? <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-3 min-w-[140px]">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${barColor}`}
                                  style={{ width: `${Math.min(lifePct, 100)}%` }}
                                />
                              </div>
                              <span className={`text-xs font-bold whitespace-nowrap ${lifePct > 80 ? 'text-rose-600' : lifePct > 60 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {lifePct.toFixed(1)}%
                              </span>
                              {lifePct > 80 && (
                                <div className="w-4 h-4 flex items-center justify-center text-rose-500 shrink-0">
                                  <i className="ri-alert-line text-xs" />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`text-sm font-bold ${remainingDanger ? 'text-rose-600' : remainingWarn ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {remainingLabel}
                            </span>
                            {remainingDanger && (
                              <span className="ml-1 text-rose-500 text-xs">⚠</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${badge.color}`}>
                              {tool.status === 'UNDER_REPAIR' && <i className="ri-tools-line text-[10px]" />}
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* View Action Button */}
                              <button
                                onClick={() => navigate(`/manufacturing/tools/${tool.id}`)}
                                className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium border border-slate-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                title="View Details"
                              >
                                <i className="ri-eye-line text-xs" /> View
                              </button>
                              {tool.status === 'ACTIVE' && (
                                <>
                                  {/* Send Repair Button */}
                                  <button
                                    onClick={() => handleSendRepair(tool)}
                                    className="px-2.5 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium border border-amber-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                    title="Send for Repair"
                                  >
                                    <i className="ri-tools-line text-xs" /> Repair
                                  </button>
                                  {/* Retire Button */}
                                  <button
                                    onClick={() => handleRetire(tool)}
                                    className="px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium border border-rose-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                    title="Retire Tool"
                                  >
                                    <i className="ri-delete-bin-line text-xs" /> Retire
                                  </button>
                                </>
                              )}
                              {tool.status === 'UNDER_REPAIR' && (
                                <button
                                  onClick={() => navigate(`/manufacturing/tools/${tool.id}`)}
                                  className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold border border-emerald-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                  title="Return from Repair"
                                >
                                  <i className="ri-logout-box-r-line text-xs" /> Return
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50/70">
              <span className="text-xs text-slate-500 font-medium">
                Showing {filtered.length} of {tools.length} tools
              </span>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}